import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ChangeDetectorRef,
  Input,
  inject,
  Injector,
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { select, Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, take } from 'rxjs/operators';
import { Chart } from 'chart.js/auto';
import { TooltipItem } from 'chart.js';
import { IListNonConformanceModel } from 'src/app/non-conformance-user/models/list-non-conformance.model';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { MOBILE_LAYOUT_QUERIES } from 'src/app/shared/constants/responsive-breakpoints';
import { datepickerValueToDate } from 'src/app/shared/functions/datepicker-value-to-date';
import { IdName } from 'src/app/shared/models/id-name';
import { DashboardDepartmentChangeService } from 'src/app/core/services/dashboard-department-change.service';
import { IDepartmentInfo } from 'src/app/shared/user-view/models/tenant-user-access.model';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { TenantUserPrivilegesService } from 'src/app/tenant-selector/services/tenant-user-privileges.service';
import { Privileges } from 'src/app/core/privileges/privileges';
import { UiState } from 'src/app/core/state/ui.state';
import { LanguageService } from 'src/app/core/services/language.service';
import { MatDialog } from '@angular/material/dialog';
import { DashboardPluginsState } from 'src/app/core/state/dashboard-plugins.state';
import { SetDeviationsOverTimeFilters } from 'src/app/core/state/dashboard-plugins.actions';
import { DeviationsOverTimeDataService } from './services/deviations-over-time-data.service';
import { DeviationsOverTimeProcessingService } from './services/deviations-over-time-processing.service';
import { DashboardListModalComponent } from './dashboard-list-modal/dashboard-list-modal.component';
import { DeviationDataPoint } from '../models/deviation-data-point.model';
import { TypeColorMapping, SelectedDeviationCount } from '../models/legend-data.model';
import { ChartScale } from '../models/chart-scale.model';
import { ChartColors } from '../models/chart-colors.model';
import { TimePeriod } from '../models/time-period.enum';
// Trend-related import - temporarily commented out, may be needed in the future
// import { TrendValue, TrendValueEnum } from 'src/app/shared/models/trend-value.model';

@Component({
  selector: 'app-deviations-over-time',
  templateUrl: './deviations-over-time.component.html',
  styleUrls: ['./deviations-over-time.component.scss'],
  standalone: false,
})
export class DeviationsOverTimeComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly injector = inject(Injector);
  readonly isMobileView = toSignal(
    inject(BreakpointObserver)
      .observe(MOBILE_LAYOUT_QUERIES)
      .pipe(map((result) => result.matches)),
    { initialValue: MOBILE_LAYOUT_QUERIES.some((query) => window.matchMedia(query).matches) }
  );

  private static readonly PERIOD_LABEL_MAP: Record<TimePeriod, string> = {
    [TimePeriod.AllTime]: 'allTime',
    [TimePeriod.YearToDate]: 'yearToDate',
    [TimePeriod.Last12Months]: 'last12Months',
    [TimePeriod.Last6Months]: 'last6Months',
    [TimePeriod.Last30Days]: 'last30Days',
    [TimePeriod.Last14Days]: 'last14Days',
    [TimePeriod.Last7Days]: 'last7Days',
    [TimePeriod.Last90Days]: 'last90Days',
    [TimePeriod.Custom]: 'custom',
  };

  @Input() openNonConformances: IListNonConformanceModel[] = [];
  @Input() completedNonConformances: IListNonConformanceModel[] = [];
  @Input() closedNonConformances: IListNonConformanceModel[] = [];

  vesselControl = new FormControl<string[]>([]);
  deviationControl = new FormControl<string[]>([]);
  timePeriodControl = new FormControl(TimePeriod.Last12Months);
  timePeriodOptions: IdName[] = [
    { id: TimePeriod.AllTime, name: 'allTime' },
    { id: TimePeriod.YearToDate, name: 'yearToDate' },
    { id: TimePeriod.Last12Months, name: 'last12Months' },
    { id: TimePeriod.Last6Months, name: 'last6Months' },
    { id: TimePeriod.Last30Days, name: 'last30Days' },
    { id: TimePeriod.Last14Days, name: 'last14Days' },
    { id: TimePeriod.Last7Days, name: 'last7Days' },
    { id: TimePeriod.Custom, name: 'custom' },
  ];

  chart: Chart | null = null;
  chartData: DeviationDataPoint[] = [];
  // Trend-related properties - temporarily commented out, may be needed in the future
  // trend: TrendValue = TrendValueEnum.Neutral;
  // trendText: string = '';
  // timePeriodLabel: string = 'Last 12 months';
  // hasInsufficientData: boolean = false;
  hasData: boolean = false;
  isLoading: boolean = false;
  isUpdatingChart = false;
  typeColorMappings: TypeColorMapping[] = [];
  uniqueTypes: string[] = [];
  availableDepartments: IDepartmentInfo[] = [];
  vesselOptions: IdName[] = [];
  allDeviationOptions: IdName[] = [];
  selectedDeviationCounts: SelectedDeviationCount[] = [];
  totalDeviationsCount: number = 0;
  areAllDeviationsSelected: boolean = true;
  selectedChartDataPoint: DeviationDataPoint | null = null;

  customRangeControl = new FormControl<{ from: Date; to: Date } | null>(null);

  private allOpenNonConformances: IListNonConformanceModel[] = [];
  private allCompletedNonConformances: IListNonConformanceModel[] = [];
  private allClosedNonConformances: IListNonConformanceModel[] = [];
  private isDarkMode = false;
  private currentLocale: string;
  private subscription = new Subscription();

  private readonly uiDarkMode = select(UiState.isDarkMode);
  private readonly uiLanguage = select(UiState.language);
  private readonly currentTenant = select(TenantUserAccessState.currentTenant);

  constructor(
    private dataService: DeviationsOverTimeDataService,
    private processingService: DeviationsOverTimeProcessingService,
    private languageService: LanguageService,
    private store: Store,
    private cdRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private dashboardDepartmentChangeService: DashboardDepartmentChangeService,
    private tenantUserPrivileges: TenantUserPrivilegesService
  ) {
    const currentLang = this.languageService.getCurrentLanguage();
    this.currentLocale = this.languageService.getLocaleForLanguage(currentLang);
  }

  ngOnInit(): void {
    const savedFilters = this.store.selectSnapshot(DashboardPluginsState.deviationsOverTimeFilters);

    // Default to the current department; keep Reports-over-time independent of other widgets.
    const defaultVesselIds = [getDepartmentId()];

    this.vesselControl.setValue(defaultVesselIds, { emitEvent: false });
    this.deviationControl.setValue((savedFilters?.deviationTypeIds || []) as string[], {
      emitEvent: false,
    });
    this.timePeriodControl.setValue(savedFilters?.timePeriod || TimePeriod.Last12Months, {
      emitEvent: false,
    });

    if (savedFilters?.customDateRange) {
      const fromDate = datepickerValueToDate(
        savedFilters.customDateRange.from as unknown as Date | string
      );
      const toDate = datepickerValueToDate(
        savedFilters.customDateRange.to as unknown as Date | string
      );
      this.customRangeControl.setValue({ from: fromDate, to: toDate }, { emitEvent: false });
    }

    this.subscription.add(
      toObservable(this.uiDarkMode, { injector: this.injector }).subscribe((darkMode: boolean) => {
        this.isDarkMode = darkMode;
        // Recreate chart with new theme colors if chart exists
        if (this.chart && this.chartData && this.chartData.length > 0) {
          this.createChart();
        }
      })
    );

    this.subscription.add(
      toObservable(this.uiLanguage, { injector: this.injector }).subscribe((language: string) => {
        this.currentLocale = this.languageService.getLocaleForLanguage(language);
        // Update the processing service with the new language before regenerating data
        this.processingService.updateLanguage(language);
        // Regenerate chartData with new language and recreate chart if data exists
        if (
          (this.allOpenNonConformances.length > 0 ||
            this.allCompletedNonConformances.length > 0 ||
            this.allClosedNonConformances.length > 0) &&
          this.hasData
        ) {
          // Regenerate chartData with new locale by reprocessing the data
          this.processData();
        } else if (this.chart && this.chartData && this.chartData.length > 0) {
          // Fallback: just recreate chart if we have chartData but no source data
          this.createChart();
        }
      })
    );

    // Load departments and update when tenant changes
    this.loadAvailableDepartments();

    // Reload departments and data when tenant changes (skip initial load)
    this.subscription.add(
      toObservable(this.currentTenant, { injector: this.injector })
        .pipe(
          filter((tenant) => !!tenant),
          distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
          skip(1) // Skip the initial emission to avoid duplicate loads on component init
        )
        .subscribe((tenant) => {
          // Reset all filters to default values when tenant changes
          const defaultVesselIds = [getDepartmentId()];
          this.vesselControl.setValue(defaultVesselIds, { emitEvent: false });
          this.deviationControl.setValue([], { emitEvent: false });
          this.timePeriodControl.setValue(TimePeriod.Last12Months, { emitEvent: false });
          this.clearCustomRangeFilters();

          // Update store with default filter values
          this.store.dispatch(
            new SetDeviationsOverTimeFilters({
              vesselIds: defaultVesselIds,
              deviationTypeIds: [],
              timePeriod: TimePeriod.Last12Months,
            })
          );

          // Reload departments for the new tenant
          // Use take(1) to ensure we only get the first emission and prevent multiple loadData() calls
          this.subscription.add(
            this.dataService
              .loadAvailableDepartments()
              .pipe(take(1))
              .subscribe((departments) => {
                this.availableDepartments = departments;
                // Convert to options format for ui-select
                this.vesselOptions = this.availableDepartments.map((dept) => ({
                  id: dept.id,
                  name: dept.name,
                }));
                // Note: Data reload happens in department change handler (tenant change triggers department change)
              })
          );
        })
    );

    // Follow the global/nav department change only; do not sync with other dashboard widgets.
    this.subscription.add(
      this.dashboardDepartmentChangeService.getDepartmentChanges().subscribe((departmentId) => {
        this.syncDepartment(departmentId);
      })
    );

    this.subscription.add(
      this.vesselControl.valueChanges.subscribe((vesselIds: string[] | null) => {
        if (vesselIds === null) {
          return;
        }

        this.deviationControl.setValue([], { emitEvent: false });
        this.store.dispatch(
          new SetDeviationsOverTimeFilters({
            vesselIds: vesselIds || [],
            deviationTypeIds: [],
          })
        );
        this.loadData();
      })
    );

    this.subscription.add(
      this.deviationControl.valueChanges.subscribe((deviationTypeIds: string[] | null) => {
        this.store.dispatch(
          new SetDeviationsOverTimeFilters({ deviationTypeIds: deviationTypeIds || [] })
        );
        this.applyFilters();
      })
    );

    // This and two above are similar, but I am keeping them separate
    // because it makes the code more readable and easier to understand
    this.subscription.add(
      this.customRangeControl.valueChanges.subscribe((range) => {
        this.handleCustomRangeChange(range);
      })
    );

    this.subscription.add(
      this.timePeriodControl.valueChanges.subscribe((timePeriod: TimePeriod | null) => {
        if (timePeriod === null || timePeriod === TimePeriod.Custom) {
          return;
        }

        this.clearCustomRangeFilters();
        this.store.dispatch(new SetDeviationsOverTimeFilters({ timePeriod }));
        if (
          this.allOpenNonConformances.length > 0 ||
          this.allCompletedNonConformances.length > 0 ||
          this.allClosedNonConformances.length > 0
        ) {
          this.applyFilters();
        } else {
          this.loadData();
        }
      })
    );

    if (
      this.openNonConformances.length === 0 &&
      this.completedNonConformances.length === 0 &&
      this.closedNonConformances.length === 0
    ) {
      this.loadData();
    } else {
      // Store the input data
      this.isLoading = true;
      this.allOpenNonConformances = [...this.openNonConformances];
      this.allCompletedNonConformances = [...this.completedNonConformances];
      this.allClosedNonConformances = [...this.closedNonConformances];
      // Initialize allDeviationOptions with all input data
      this.allDeviationOptions = this.dataService.updateAllDeviationOptions([
        ...this.allOpenNonConformances,
        ...this.allCompletedNonConformances,
        ...this.allClosedNonConformances,
      ]);
      // Use setTimeout to ensure filters are applied after view initialization
      setTimeout(() => {
        this.applyFilters();
      });
    }
  }

  private loadAvailableDepartments(): void {
    this.subscription.add(
      this.dataService.loadAvailableDepartments().subscribe((departments) => {
        this.availableDepartments = departments;
        // Convert to options format for ui-select
        this.vesselOptions = this.availableDepartments.map((dept) => ({
          id: dept.id,
          name: dept.name,
        }));
      })
    );
  }

  private syncDepartment(departmentId: string): void {
    const currentSelection = this.vesselControl.value || [];
    if (currentSelection.length === 1 && currentSelection[0] === departmentId) {
      return;
    }

    const defaultVesselIds = [departmentId];
    this.vesselControl.setValue(defaultVesselIds, { emitEvent: false });
    this.deviationControl.setValue([], { emitEvent: false });
    this.store.dispatch(
      new SetDeviationsOverTimeFilters({
        vesselIds: defaultVesselIds,
        deviationTypeIds: [],
      })
    );
    this.loadData();
  }

  ngAfterViewInit(): void {
    // Use setTimeout to ensure DOM is fully rendered before creating chart
    setTimeout(() => {
      if (this.hasData && this.chartData && this.chartData.length > 0) {
        this.createChart();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.destroyChart();
  }

  private loadData(): void {
    // Prevent multiple simultaneous loadData() calls
    if (this.isLoading) {
      return;
    }

    if (!this.tenantUserPrivileges.userHasPrivilege(Privileges.NonConformanceRead)) {
      return;
    }

    const selectedVesselIds = this.vesselControl.value || [];
    this.isLoading = true;
    this.subscription.add(
      this.dataService.loadData(selectedVesselIds).subscribe({
        next: (data) => {
          this.allOpenNonConformances = data.open;
          this.allCompletedNonConformances = data.completed;
          this.allClosedNonConformances = data.closed;
          // Initialize allDeviationOptions with all data
          this.allDeviationOptions = this.dataService.updateAllDeviationOptions([
            ...this.allOpenNonConformances,
            ...this.allCompletedNonConformances,
            ...this.allClosedNonConformances,
          ]);
          this.applyFilters();
        },
        error: (error) => {
          console.error('Failed to load deviations data:', error);
          this.isLoading = false;
        },
      })
    );
  }

  private applyFilters(): void {
    const selectedVesselIds = this.vesselControl.value || [];
    const selectedTypeIds = (this.deviationControl.value || []) as string[];

    // Get department-filtered data first (before type filtering)
    // This ensures the dropdown always shows ALL available types for the current departments
    const departmentFilteredData = this.dataService.applyFilters(
      this.allOpenNonConformances,
      this.allCompletedNonConformances,
      this.allClosedNonConformances,
      selectedVesselIds,
      [], // Don't filter by type yet - we need all types for the dropdown
      this.allDeviationOptions
    );

    // Update allDeviationOptions based on department-filtered data
    this.allDeviationOptions = this.dataService.updateAllDeviationOptions([
      ...departmentFilteredData.open,
      ...departmentFilteredData.completed,
      ...departmentFilteredData.closed,
    ]);

    // Apply both department and type filters in a single call
    const filtered = this.dataService.applyFilters(
      this.allOpenNonConformances,
      this.allCompletedNonConformances,
      this.allClosedNonConformances,
      selectedVesselIds,
      selectedTypeIds,
      this.allDeviationOptions
    );

    this.openNonConformances = filtered.open;
    this.completedNonConformances = filtered.completed;
    this.closedNonConformances = filtered.closed;

    this.processData();
  }

  private processData(): void {
    this.selectedChartDataPoint = null;
    const allNonConformances = [
      ...this.openNonConformances,
      ...this.completedNonConformances,
      ...this.closedNonConformances,
    ];
    const period = this.timePeriodControl.value || TimePeriod.Last30Days;

    this.uniqueTypes = this.dataService.extractUniqueTypes(allNonConformances);
    this.typeColorMappings = this.processingService.generateTypeColorMappings(this.uniqueTypes);

    // Get custom date range if period is Custom
    const savedFilters = this.store.selectSnapshot(DashboardPluginsState.deviationsOverTimeFilters);
    const customDateRange =
      period === TimePeriod.Custom && savedFilters?.customDateRange
        ? savedFilters.customDateRange
        : undefined;

    this.chartData = this.processingService.groupDataByPeriod(
      allNonConformances,
      period,
      customDateRange
    );

    // Calculate legend data to get the actual total count after all filtering
    const legendData = this.processingService.calculateLegendData(
      this.chartData,
      (this.deviationControl.value || []) as string[],
      this.allDeviationOptions,
      this.typeColorMappings
    );
    this.totalDeviationsCount = legendData.totalDeviationsCount;
    this.selectedDeviationCounts = legendData.selectedDeviationCounts;
    this.areAllDeviationsSelected = legendData.areAllDeviationsSelected;

    // Check if we have any actual data to display (after all filtering)
    // hasData should be false if:
    // 1. No non-conformances at all
    // 2. Chart data is empty
    // 3. Total deviations count is 0 (meaning all data was filtered out)
    const hasActualData =
      allNonConformances.length > 0 && this.chartData.length > 0 && this.totalDeviationsCount > 0;

    // If no data, clear chart and reset values
    if (!hasActualData) {
      this.hasData = false;
      this.chartData = [];
      this.totalDeviationsCount = 0;
      this.selectedDeviationCounts = [];
      this.areAllDeviationsSelected = true;
      // Trend-related code - temporarily commented out, may be needed in the future
      // this.trend = TrendValueEnum.Neutral;
      // this.trendText = '';
      // this.hasInsufficientData = false;
      this.destroyChart();
      // this.updateTimePeriodLabel();
      this.isLoading = false;
      return;
    }

    this.hasData = true;

    // Trend calculation - temporarily commented out, may be needed in the future
    // const trendResult = this.processingService.calculateTrend(
    //   this.chartData,
    //   period,
    //   this.allOpenNonConformances,
    //   this.allCompletedNonConformances,
    //   this.allClosedNonConformances,
    //   this.vesselControl.value || [],
    //   (this.deviationControl.value || []) as string[],
    //   this.allDeviationOptions,
    //   customDateRange
    // );
    // this.trend = trendResult.trend;
    // this.trendText = trendResult.trendText;
    // this.hasInsufficientData = trendResult.hasInsufficientData;

    // this.updateTimePeriodLabel();
    this.updateChart();
    this.isLoading = false;
  }

  private createChart(): void {
    if (this.isUpdatingChart || !this.chartData || this.chartData.length === 0) {
      return;
    }

    const canvas = document.getElementById('reportsChart') as HTMLCanvasElement;
    if (!canvas) {
      return;
    }

    this.isUpdatingChart = true;
    this.destroyChart();

    const labels = this.processingService.translateMonthLabels(this.chartData.map((d) => d.period));
    const totalData = this.chartData.map((d) => d.total);
    const chartScale = this.processingService.calculateChartScale(totalData);
    const chartColors = this.processingService.getChartColors();

    try {
      this.chart = new Chart(canvas, {
        type: 'bar',
        data: {
          labels,
          datasets: [this.createLineDataset(totalData, chartColors), ...this.createBarDatasets()],
        },
        options: this.createChartOptions(chartScale, chartColors),
        plugins: [this.createChartPlugin()],
      });
    } finally {
      this.isUpdatingChart = false;
    }
  }

  private createLineDataset(totalData: number[], chartColors: ChartColors) {
    return {
      type: 'line' as const,
      label: 'Total Reports',
      data: totalData,
      borderColor: chartColors.lineBlue,
      backgroundColor: (context: { chart: Chart }) => {
        const chart = context.chart;
        const { ctx, chartArea } = chart;
        if (!chartArea) {
          return chartColors.lineBlueOpacity40;
        }
        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        gradient.addColorStop(0, chartColors.lineBlueOpacity90);
        gradient.addColorStop(0.4, chartColors.lineBlueOpacity60);
        gradient.addColorStop(0.7, chartColors.lineBlueOpacity30);
        gradient.addColorStop(1, chartColors.lineBlueOpacity10);
        return gradient;
      },
      borderWidth: 2,
      pointRadius: 4,
      pointBackgroundColor: chartColors.pointBackground,
      pointBorderColor: chartColors.lineBlue,
      pointBorderWidth: 2,
      fill: false,
      order: -1000,
    };
  }

  private createBarDatasets() {
    return this.typeColorMappings.map((mapping, index) => {
      const typeData = this.chartData.map((d) => d.typeCounts.get(mapping.typeName) || 0);
      return {
        label: mapping.typeName,
        data: typeData,
        backgroundColor: mapping.color,
        order: index + 1,
        stack: 'stack1',
      };
    });
  }

  private createChartOptions(chartScale: ChartScale, chartColors: ChartColors) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
          filter: (tooltipItem: TooltipItem<'bar' | 'line'>) => {
            if (tooltipItem.datasetIndex === 0) {
              return true;
            }
            const yValue = tooltipItem.parsed.y;
            return yValue !== null && yValue !== undefined && yValue > 0;
          },
        },
      },
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      onClick: (
        event: unknown,
        activeElements: Array<{ datasetIndex: number; index: number; element: unknown }>
      ) => {
        this.handleChartClick(activeElements);
      },
      onHover: (
        event: unknown,
        activeElements: Array<{ datasetIndex: number; element: { y: number } }>,
        chart: Chart
      ) => {
        this.handleChartHover(
          event as { native: { target: HTMLCanvasElement; clientY: number } },
          activeElements
        );
      },
      scales: {
        y: {
          beginAtZero: true,
          max: chartScale.max,
          stacked: true,
          ticks: {
            stepSize: chartScale.stepSize,
            color: this.isDarkMode ? chartColors.axisTextDark : chartColors.axisTextLight,
          },
          grid: {
            color: this.isDarkMode ? chartColors.axisGridDark : chartColors.axisGridLight,
          },
        },
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            color: this.isDarkMode ? chartColors.axisTextDark : chartColors.axisTextLight,
          },
        },
      },
    };
  }

  private handleChartClick(
    activeElements: Array<{ datasetIndex: number; index: number; element: unknown }>
  ): void {
    if (!activeElements || activeElements.length === 0) {
      this.selectedChartDataPoint = null;
      return;
    }

    const activeElement =
      activeElements.find((element) => element.datasetIndex > 0 && element.element) ??
      activeElements[0];
    const dataPoint = this.chartData[activeElement.index];

    if (!this.isMobileView()) {
      if (dataPoint?.items?.length > 0) {
        this.openNonConformancesModal(dataPoint.period, dataPoint.items);
      }
      return;
    }

    this.selectedChartDataPoint = dataPoint?.items?.length > 0 ? dataPoint : null;
    this.cdRef.detectChanges();
  }

  viewSelectedReports(): void {
    if (!this.selectedChartDataPoint) {
      return;
    }

    this.openNonConformancesModal(
      this.selectedChartDataPoint.period,
      this.selectedChartDataPoint.items
    );
  }

  private handleChartHover(
    event: { native: { target: HTMLCanvasElement; clientY: number } },
    activeElements: Array<{ datasetIndex: number; element: { y: number } }>
  ): void {
    const canvas = event.native?.target;
    if (!canvas) {
      return;
    }

    if (!activeElements || activeElements.length === 0) {
      canvas.style.cursor = 'default';
      return;
    }

    const barElements = activeElements.filter((el) => el.datasetIndex > 0 && el.element);
    if (barElements.length === 0) {
      canvas.style.cursor = 'default';
      return;
    }

    const chartArea = this.chart?.chartArea;
    if (!chartArea) {
      canvas.style.cursor = 'default';
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const mouseY = event.native.clientY - rect.top;

    const topBar = barElements.reduce(
      (top, current) => {
        if (!top) return current;
        return current.element.y < top.element.y ? current : top;
      },
      null as (typeof barElements)[0] | null
    );

    if (topBar?.element) {
      const isOverBar = mouseY >= topBar.element.y && mouseY <= chartArea.bottom;
      canvas.style.cursor = isOverBar ? 'pointer' : 'default';
    } else {
      canvas.style.cursor = 'default';
    }
  }

  private createChartPlugin() {
    return {
      id: 'zIndexFix',
      beforeDraw: (chart: Chart, _args: unknown, _options: unknown) => {
        const lineDatasetIndex = chart.data.datasets.findIndex((ds) => ds.type === 'line');
        if (lineDatasetIndex < 0) {
          return;
        }

        const lineMeta = chart.getDatasetMeta(lineDatasetIndex);
        if (!lineMeta?.data?.length) {
          return;
        }

        const ctx = chart.ctx;
        ctx.save();

        const lineDataset = chart.data.datasets[lineDatasetIndex];
        const fillStyle =
          typeof lineDataset.backgroundColor === 'function'
            ? (lineDataset.backgroundColor as (context: unknown) => string | CanvasGradient)({
                chart,
              })
            : lineDataset.backgroundColor;

        ctx.fillStyle = fillStyle as string | CanvasGradient;
        ctx.beginPath();

        const firstPoint = lineMeta.data[0];
        const lastPoint = lineMeta.data[lineMeta.data.length - 1];

        if (firstPoint && lastPoint && chart.chartArea) {
          ctx.moveTo(firstPoint.x, chart.chartArea.bottom);

          lineMeta.data.forEach((point) => {
            if (point && point.x !== undefined && point.y !== undefined) {
              ctx.lineTo(point.x, point.y);
            }
          });

          ctx.lineTo(lastPoint.x, chart.chartArea.bottom);
          ctx.closePath();
          ctx.fill();
        }

        ctx.restore();
      },
    };
  }

  private updateChart(): void {
    if (this.isUpdatingChart) {
      return;
    }
    // Use setTimeout to ensure DOM is ready before creating/updating chart
    setTimeout(() => {
      if (this.hasData && this.chartData && this.chartData.length > 0 && !this.isUpdatingChart) {
        this.createChart();
      } else {
        this.destroyChart();
      }
    });
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  // Trend-related method - temporarily commented out, may be needed in the future
  // private updateTimePeriodLabel(): void {
  //   const period = this.timePeriodControl.value || TimePeriod.Last12Months;
  //   if (period === TimePeriod.Custom) {
  //     const customRangeText = this.getCustomRangeDisplayText(this.customRangeControl.value);
  //     this.timePeriodLabel =
  //       customRangeText || DeviationsOverTimeComponent.PERIOD_LABEL_MAP[period] || 'custom';
  //     return;
  //   }
  //   this.timePeriodLabel = DeviationsOverTimeComponent.PERIOD_LABEL_MAP[period] || 'last12Months';
  // }

  private openNonConformancesModal(period: string, items: IListNonConformanceModel[]): void {
    this.dialog.open(DashboardListModalComponent, {
      data: {
        period,
        items,
      },
      width: '60rem',
      maxWidth: '90vw',
      panelClass: 'mobile-sizing-for-mat-dialogs',
    });
  }

  private handleCustomRangeChange(range: { from: Date; to: Date } | null): void {
    if (range) {
      this.timePeriodControl.setValue(TimePeriod.Custom, { emitEvent: false });
      this.store.dispatch(
        new SetDeviationsOverTimeFilters({
          timePeriod: TimePeriod.Custom,
          customDateRange: range,
        })
      );
      this.refreshData();
      return;
    }

    this.store.dispatch(
      new SetDeviationsOverTimeFilters({
        customDateRange: undefined,
      })
    );

    if (this.timePeriodControl.value === TimePeriod.Custom) {
      this.timePeriodControl.setValue(TimePeriod.Last12Months, { emitEvent: false });
      this.store.dispatch(
        new SetDeviationsOverTimeFilters({
          timePeriod: TimePeriod.Last12Months,
        })
      );
    }
    this.refreshData();
  }

  private clearCustomRangeFilters(): void {
    this.customRangeControl.setValue(null, { emitEvent: false });
    this.store.dispatch(
      new SetDeviationsOverTimeFilters({
        customDateRange: undefined,
      })
    );
  }

  private refreshData(): void {
    if (
      this.allOpenNonConformances.length > 0 ||
      this.allCompletedNonConformances.length > 0 ||
      this.allClosedNonConformances.length > 0
    ) {
      this.applyFilters();
      return;
    }
    this.loadData();
  }

  private getCustomRangeDisplayText(range: { from: Date; to: Date } | null): string {
    if (!range) {
      return '';
    }
    const fromFormatted = this.formatDateShort(range.from);
    const toFormatted = this.formatDateShort(range.to);
    return `${fromFormatted} – ${toFormatted}`;
  }

  private formatDateShort(date: Date): string {
    return new Intl.DateTimeFormat(this.currentLocale, {
      month: 'short',
      day: '2-digit',
    }).format(date);
  }
}
